/**
 * Intangible development projects — user-facing error copy (ADR 0017, epic ALC-3485).
 *
 * Only the error map ships in slice ALC-3486; the screens land in slice ALC-3491 and add their
 * page copy to this same namespace. The codes are here ahead of the UI on purpose: the API
 * refuses with a stable code and an already-usable pt-BR message from day one, so a client
 * calling it directly — or the next slice's first render — never shows a raw code.
 *
 * Every message names the accounting reason, not just the rule. "Faltam condições" tells an
 * accountant nothing; naming CPC 04 §57 and the missing conditions tells them what to go and
 * establish.
 */
export const assetsIntangiblePtBr = {
  errors: {
    INTANGIBLE_PROJECT_NOT_FOUND: 'Projeto de intangível não encontrado.',
    INTANGIBLE_PROJECT_ENTITY_NOT_FOUND: 'Empresa não encontrada.',
    INTANGIBLE_PROJECT_CLASS_NOT_FOUND: 'Classe de ativo não encontrada ou inativa.',
    INTANGIBLE_PROJECT_CLASS_NOT_INTANGIBLE:
      'Esta classe não pertence ao grupo Intangível. Um projeto de desenvolvimento capitaliza ' +
      'custo sob o CPC 04, então precisa apontar para uma classe de intangível.',
    INTANGIBLE_PROJECT_STATUTORY_BOOK_REQUIRED:
      'Esta empresa não possui livro ativo no framework da classe escolhida. Configure o livro ' +
      'antes de abrir um projeto de desenvolvimento intangível.',
    INTANGIBLE_PROJECT_FRAMEWORK_NOT_ON_ENTITY:
      'Esta classe pertence a uma norma contábil que a empresa não mantém em nenhum livro ' +
      'ativo. Um projeto medido sob uma norma que a empresa não reporta certificaria as ' +
      'condições do checklist contra o padrão errado.',
    INTANGIBLE_PROJECT_CODE_DUPLICATE:
      'Já existe um projeto com esse código. Use outro identificador.',
    INTANGIBLE_FISCAL_BOOK_AMBIGUOUS:
      'Esta empresa tem mais de um livro fiscal (TAX_BASIS) ativo no seu escopo de mensuração, ' +
      'e a exclusão do art. 42 precisa de um livro só. Abra Configurações → Contábil & Fiscal ' +
      '→ Livros, deixe apenas um deles ativo para esta empresa e repita o lançamento.',
    INTANGIBLE_FISCAL_BOOK_MISSING_AFTER_EXCLUSION:
      'Este projeto já excluiu custo do lucro real pelo art. 42 da Lei 12.973, mas esta empresa ' +
      'não tem mais nenhum livro fiscal (TAX_BASIS) ativo no seu escopo de mensuração. Sem o ' +
      'livro, a adição que devolve essa exclusão não tem onde ser lançada, e o mesmo custo ' +
      'seria deduzido duas vezes. Abra Configurações → Contábil & Fiscal → Livros, reative o ' +
      'livro fiscal desta empresa (ou inclua a empresa de novo no escopo dele) e repita a ' +
      'operação.',
    INTANGIBLE_PROJECT_REVERSAL_AFTER_CLOSE:
      'Este projeto já saiu de pesquisa/desenvolvimento, então o custo dele não pode mais ser ' +
      'estornado pelo documento de origem. A exclusão do art. 42 seria retirada, mas a base ' +
      'fiscal do bem foi congelada no go-live e não é recalculada — as adições continuariam ' +
      'pelo valor antigo e a diferença temporária viraria definitiva, com imposto pago a mais ' +
      'para sempre. Faça a correção no próprio bem (baixa ou revisão do ativo), que ajusta as ' +
      'duas pontas; se o documento precisa mesmo ser estornado, isso tem de acontecer antes do ' +
      'go-live do projeto.',
    INTANGIBLE_PROJECT_CLOSED:
      'Este projeto já foi colocado em uso ou baixado e não aceita mais alterações. Trabalhos ' +
      'posteriores ao go-live são despesa (CPC 04 §20); uma nova versão é um novo projeto.',
    INTANGIBLE_PROJECT_DATE_INVALID: 'Informe uma data válida no formato AAAA-MM-DD.',
    INTANGIBLE_GATE_TEMPLATE_MISSING:
      'Não há checklist de capitalização cadastrado para o framework contábil deste projeto. ' +
      'Sem as condições da norma não é possível abrir o gate de desenvolvimento.',
    INTANGIBLE_GATE_INCOMPLETE:
      'Todas as seis condições do CPC 04 §57 precisam ser atendidas para capitalizar o ' +
      'desenvolvimento. Enquanto faltar alguma, o custo do projeto continua em despesa.',
    INTANGIBLE_GATE_ITEM_UNKNOWN:
      'Uma das condições informadas não pertence ao checklist do framework deste projeto.',
    INTANGIBLE_GATE_NOT_RETROACTIVE:
      'O início do desenvolvimento só pode ser adiado, nunca antecipado: a capitalização vale ' +
      'da competência informada em diante e recuar a data pediria a competências já encerradas ' +
      'que reclassificassem despesa já publicada.',
    INTANGIBLE_GATE_BEFORE_PROJECT_START:
      'O início do desenvolvimento não pode ser anterior à competência em que o projeto foi ' +
      'criado — não há custo acompanhado antes dessa data.',
    INTANGIBLE_GATE_RECERTIFICATION_REASON_REQUIRED:
      'Informe o motivo para alterar o início do desenvolvimento. A certificação anterior é ' +
      'mantida, e o motivo é o que explica, na auditoria, por que a competência a partir da ' +
      'qual o custo capitaliza mudou.',
    INTANGIBLE_ENTITY_POLICY_NO_FISCAL_BOOK:
      'Esta empresa não possui livro fiscal (TAX_BASIS) ativo. A exclusão do art. 42 da Lei ' +
      '12.973/2014 é registrada no livro fiscal, então não há onde aplicá-la aqui.',
    INTANGIBLE_ENTITY_POLICY_NOT_RETROACTIVE:
      'A alteração da política do art. 42 vale a partir do momento em que é feita e não pode ' +
      'ser retroagida: os projetos criados sob a resposta anterior já congelaram a eleição ' +
      'deles, e mudá-la agora reescreveria decisões passadas.',
    INTANGIBLE_IN_PROGRESS_ACCOUNT_UNRESOLVED:
      'A conta do slot Intangível em Andamento não está mapeada para esta versão do plano de ' +
      'contas, ou aponta para uma conta que não é de ativo (débito, não redutora, passível de ' +
      'lançamento). Ajuste o mapeamento antes de capitalizar custo de desenvolvimento.',

    // ── Fila de revisão: veredito ADD_TO_PROJECT (ALC-3487, ADR 0017 D3) ─────────────────
    INTANGIBLE_PROJECT_REQUIRED:
      'Informe o projeto de destino para encaminhar esta linha a um projeto de desenvolvimento.',
    INTANGIBLE_PROJECT_NOT_OPEN:
      'Este projeto já foi colocado em uso ou baixado e não recebe mais custo. Trabalho ' +
      'posterior ao go-live é despesa (CPC 04 §20); uma nova versão é um novo projeto.',
    // Raised by both rails: the review queue routing a bill line (ALC-3487) and the allocation
    // grid pointing a person at a project (ALC-3488). The message names both sources because
    // one code cannot know which screen the reader is on.
    INTANGIBLE_PROJECT_ENTITY_MISMATCH:
      'O projeto pertence a outra empresa que não a da conta a pagar — ou, numa alocação, que ' +
      'não a empregadora da pessoa. O custo de um projeto é da empresa que o desenvolve, e o ' +
      'custo de uma pessoa só capitaliza no balanço da empresa que a paga; ligar os dois ' +
      'moveria valor entre empresas sem lançamento intercompany.',
    INTANGIBLE_PROJECT_BILL_ALREADY_POSTED:
      'Esta conta já foi contabilizada, então vinculá-la agora a um projeto exigiria um ' +
      'lançamento de reclassificação. Devolva a conta para aprovação (isso estorna a ' +
      'contabilização), decida este candidato e aprove a conta de novo.',
    INTANGIBLE_PROJECT_CLASSIFICATION_MULTIPLE_PROJECTS:
      'A mesma classificação encaminha linhas para mais de um projeto. Como o lançamento ' +
      'carrega a classificação e nada mais fino, todo o débito iria para um só projeto. ' +
      'Classifique a conta por linha.',
    INTANGIBLE_PROJECT_BOOK_NOT_POSTED:
      'A conta não gerou lançamento no livro da norma sob a qual o projeto é medido, então o ' +
      'custo do projeto ficaria sem contrapartida no razão.',
    INTANGIBLE_PROJECT_FRAMEWORK_NOT_POSTED:
      'O projeto é medido em um framework que esta empresa mantém apenas como livro memorando. ' +
      'A provisão da conta só gera lançamento no livro contabilizado, então não há onde ' +
      'registrar o custo. Use um projeto medido no framework do livro contabilizado da empresa.',
    INTANGIBLE_PROJECT_FRAMEWORK_MULTIPLE_BOOKS:
      'A empresa tem mais de um livro contabilizado na norma sob a qual o projeto é medido, e o ' +
      'custo do projeto precisa apontar para um razão só. Ajuste as normas dos livros da ' +
      'empresa antes de contabilizar.',

    // ── Go-live e baixa do projeto (ALC-3489, ADR 0017 D6, D7, D8) ──────────────────────
    INTANGIBLE_PROJECT_NO_CAPITALISED_COST:
      'Este projeto não tem custo capitalizado. Ou o gate do CPC 04 §57 nunca foi aberto, ou ' +
      'todo o gasto é anterior a ele e permanece em despesa — em nenhum dos casos há ativo a ' +
      'reconhecer. Abra o gate e capitalize ao menos uma competência antes de colocar o ' +
      'projeto em uso, ou baixe o projeto.',
    INTANGIBLE_PROJECT_GO_LIVE_BEFORE_LAST_COST:
      'A data de entrada em uso é anterior à última competência que capitalizou custo. O ativo ' +
      'nasce com todo o custo capitalizado e amortiza a partir da entrada em uso, então ' +
      'colocá-lo em uso antes do mês do próprio custo amortizaria uma base que o razão ainda ' +
      'não tinha formado.',
    INTANGIBLE_PROJECT_BALANCE_MISMATCH:
      'O saldo de Intangível em Andamento deste projeto no razão não bate com o custo ' +
      'capitalizado registrado no projeto. Enquanto os dois discordarem, encerrar o projeto ' +
      'lançaria a diferença contra um projeto que deixa de existir — e só um lançamento manual ' +
      'poderia limpá-la depois. Concilie a conta antes de colocar o projeto em uso ou baixá-lo.',
    INTANGIBLE_PROJECT_GO_LIVE_DIMENSION_AMBIGUOUS:
      'Os custos deste projeto foram lançados em mais de um centro de custo, departamento ou ' +
      'conta gerencial. Coloque o projeto em uso individualmente e escolha em qual o ativo ' +
      'será amortizado — a amortização de toda a vida útil segue a dimensão do ativo.',
    INTANGIBLE_PROJECT_GO_LIVE_DIMENSION_WRONG_ENTITY:
      'A dimensão escolhida não pertence à empresa do projeto. Escolha um centro de custo, ' +
      'departamento ou conta gerencial da mesma empresa.',
    INTANGIBLE_BOOK_DATE_AMBIGUOUS:
      'Os livros desta empresa estão em fusos horários diferentes e, agora, em dias diferentes. ' +
      'Informe a data do lançamento.',
    INTANGIBLE_PROJECT_LIFE_OVERRIDE_REASON_REQUIRED:
      'Informe o motivo para usar uma vida útil diferente da regra da classe. A vida útil ' +
      'decide a amortização de todos os meses seguintes (CPC 04 §97), então divergir da régua ' +
      'da classe é um julgamento que a auditoria precisa poder ler no cadastro do bem.',
    INTANGIBLE_PROJECT_WRITE_OFF_REASON_REQUIRED:
      'Informe o motivo da baixa. O desreconhecimento de um projeto de desenvolvimento é uma ' +
      'decisão de julgamento (CPC 04 §112), não um lançamento de rotina: um saldo movido para ' +
      'conta de perda sem nada explicando por quê é inauditável.',
    INTANGIBLE_PROJECT_WRITE_OFF_BEFORE_LAST_COST:
      'A data da baixa é anterior à última competência que capitalizou custo. A baixa credita ' +
      'todo o saldo de Intangível em Andamento do projeto na competência em que for lançada, e ' +
      'nessa competência o razão ainda não tinha recebido esse saldo inteiro — a conta fecharia ' +
      'o mês negativa. A baixa pertence ao período corrente (CPC 04 §112).',
    INTANGIBLE_PROJECT_ASSET_BOOK_RULE_MISSING:
      'A classe do projeto não tem régua de amortização no livro que recebe a reclassificação ' +
      'do go-live. Sem régua, o bem entraria em uso sem amortização societária. Cadastre a ' +
      'regra da classe para esse livro antes de colocar o projeto em uso.',
    INTANGIBLE_PROJECT_BOOK_CURRENCY_CONFLICT:
      'Os livros que contabilizam o framework deste projeto têm moedas funcionais diferentes. ' +
      'Um projeto vira exatamente um ativo, e um ativo tem um único custo em uma única moeda — ' +
      'não há como registrá-lo em duas. Unifique a moeda funcional dos livros antes de colocar ' +
      'o projeto em uso.',
    INTANGIBLE_WRITE_OFF_LOSS_ACCOUNT_UNRESOLVED:
      'A conta do slot Perda na Baixa de Projeto Intangível não está mapeada para esta versão ' +
      'do plano de contas. Ajuste o mapeamento antes de baixar um projeto de desenvolvimento.',
    INTANGIBLE_WRITE_OFF_LOSS_ACCOUNT_TYPE_INVALID:
      'O slot Perda na Baixa de Projeto Intangível aponta para uma conta que não é de despesa ' +
      '(débito, não redutora, passível de lançamento, tipo Despesa ou Outras Despesas). A ' +
      'baixa de um projeto é sempre perda no resultado (CPC 04 §112); corrija o mapeamento.',
    ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_INTANGIBLE:
      'Vida útil indefinida só existe para classes do grupo Intangível (CPC 04 §107). ' +
      'Imobilizado e direito de uso são sempre depreciados ou amortizados; ajuste a régua da ' +
      'classe em vez de marcar a vida útil como indefinida.',
    ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_NONE:
      'Um intangível de vida útil indefinida não é amortizado (CPC 04 §107) — é testado quanto ' +
      'à recuperabilidade uma vez por ano. A régua societária da classe precisa usar o método ' +
      'NONE: marcar a classe sem parar a régua deixaria a divulgação dizendo uma coisa e a ' +
      'mensuração fazendo outra.',
    INTANGIBLE_PROJECT_CLASSIFICATION_COVERS_UNROUTED_LINES:
      'A classificação que encaminha custo ao projeto também cobre linhas desta conta que não ' +
      'foram encaminhadas a ele, e o débito da classificação inteira iria para o intangível em ' +
      'andamento. Classifique a conta por linha antes de contabilizar.',
    INTANGIBLE_PROJECT_PAYROLL_BILL_NOT_ROUTABLE:
      'Esta conta não é encaminhada a projetos pela fila de revisão: ou é uma conta de folha, ' +
      'ou é a nota mensal de um colaborador PJ cadastrado. Esse custo chega ao projeto pelo ' +
      'rateio padrão do próprio projeto — encaminhar aqui contaria o mesmo custo duas vezes.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_DEFERRAL:
      'Esta conta está encaminhada a um projeto de desenvolvimento e também tem uma ' +
      'apropriação ativa. O custo que constrói um ativo capitaliza no projeto e amortiza a ' +
      'partir do go-live — cancele uma das duas antes de contabilizar.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_CLIENT_REIMBURSABLE:
      'Esta conta é reembolsável pelo cliente e também está encaminhada a um projeto de ' +
      'desenvolvimento. Custo repassado ao cliente não é custo de desenvolvimento próprio.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_CAPITALIZATION:
      'Uma mesma classificação desta conta capitaliza um bem e também encaminha custo a um ' +
      'projeto de desenvolvimento. Um custo vira um bem próprio ou um mês de projeto, nunca os ' +
      'dois — classifique a conta por linha para que cada decisão chegue à sua própria conta.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_LEASE_SETTLEMENT:
      'Esta conta liquida parcela de arrendamento e também está encaminhada a um projeto de ' +
      'desenvolvimento. A parcela liquida uma obrigação já no balanço; não constrói um ativo novo.',

    // The same four conflicts, on the rail that moves a percentage instead of a whole
    // classification (ALC-3488 R1).
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_DEFERRAL:
      'Esta conta é de um colaborador PJ com rateio de alocação na competência e também tem ' +
      'diferimento ativo. O rateio levaria uma fração de um débito que ainda nem é despesa do ' +
      'período — cancele o diferimento ou retire a alocação da competência.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_CLIENT_REIMBURSABLE:
      'Esta conta é reembolsável pelo cliente e também é de um colaborador PJ com rateio de ' +
      'alocação na competência. Custo repassado ao cliente não é custo de desenvolvimento ' +
      'próprio — marque a conta como não reembolsável ou zere a alocação da competência.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_LEASE_SETTLEMENT:
      'Esta conta liquida uma parcela de arrendamento e também é de um colaborador PJ com ' +
      'rateio de alocação na competência. A parcela baixa uma obrigação que já está no ' +
      'balanço; não constrói um ativo novo.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_CAPITALIZATION:
      'Uma mesma classificação desta conta capitaliza um bem e também entra no rateio de ' +
      'alocação do colaborador PJ. O rateio levaria uma fração do custo do bem para o ' +
      'intangível em andamento — classifique a conta por linha ou cancele a capitalização.',

    // The allocation grid (slice ALC-3488). Every code the grid endpoints can raise, so no
    // refusal reaches the operator as a raw string.
    INTANGIBLE_ALLOCATION_COMPETENCIA_INVALID:
      'A competência precisa estar no formato AAAA-MM, por exemplo 2026-06.',
    INTANGIBLE_ALLOCATION_PERIOD_POSTED:
      'A folha desta competência já foi contabilizada. Salvar a alocação agora gera um ' +
      'lançamento de reclassificação no período aberto atual — confirme para continuar. A ' +
      'folha em si não é reaberta.',
    INTANGIBLE_ALLOCATION_CORRECTION_REQUIRES_GL:
      'A folha desta competência já foi contabilizada, e corrigir a alocação agora gera um ' +
      'lançamento contábil. Peça a quem tem permissão de contabilidade para salvar esta correção.',
    INTANGIBLE_ALLOCATION_PROJECT_DUPLICATE:
      'O mesmo projeto aparece duas vezes na linha desta pessoa. Some os percentuais em uma ' +
      'única célula.',
    INTANGIBLE_ALLOCATION_PERCENT_INVALID:
      'Cada percentual precisa ser maior que zero e no máximo 100. Para tirar a pessoa de um ' +
      'projeto, apague a célula em vez de digitar zero.',
    INTANGIBLE_ALLOCATION_OVER_100:
      'A soma dos percentuais desta pessoa na competência passa de 100%. Uma pessoa não pode ' +
      'ser alocada além do próprio custo.',
    INTANGIBLE_ALLOCATION_PERSON_NOT_FOUND:
      'Esta pessoa não foi encontrada ou não está mais ativa. Recarregue a grade.',
    INTANGIBLE_ALLOCATION_CORRECTION_NOT_SUPPORTED_FOR_PJ:
      'Alocações de colaboradores PJ não geram lançamento de reclassificação: o custo chega ' +
      'por nota fiscal. Reclassifique a fatura no contas a pagar.',
    INTANGIBLE_ALLOCATION_ENTITY_MISMATCH:
      'A empresa selecionada não é a empregadora desta pessoa. O custo de um colaborador CLT ' +
      'só capitaliza no balanço da empresa que o paga: troque a empresa da grade para a ' +
      'empregadora antes de alocar.',
    INTANGIBLE_ALLOCATION_CORRECTION_BASE_UNRESOLVED:
      'Não foi possível identificar a conta de despesa que recebeu o custo desta pessoa na ' +
      'competência. Confira o mapeamento de dimensões e das contas de folha (salários, ' +
      'encargos e provisões) antes de corrigir a alocação.',
    GL_BOOK_CURRENCY_UNSUPPORTED:
      'O livro desta empresa não tem uma moeda funcional suportada configurada, então a ' +
      'reclassificação de alocação não pode ser lançada.',
    JOURNAL_NO_OPEN_PERIOD:
      'Não há período contábil aberto para receber a reclassificação da alocação. A folha da ' +
      'competência não é reaberta — abra o período corrente e salve de novo.',
  },
  /**
   * The monthly allocation grid (ADR 0017 D4/D11, slice ALC-3488).
   *
   * Not one string here names money, and none may be added. The screen shows names and
   * percentages; what those percentages are worth is resolved when the payroll run posts and
   * is only ever shown as a project total.
   */
  allocations: {
    loading: 'Carregando alocações…',
    competenciaLabel: 'Competência',
    copyPrevious: 'Copiar mês anterior',
    copying: 'Copiando…',
    copySuccess: 'Mês anterior copiado.',
    /**
     * A cópia SUBSTITUI a competência, não soma a ela — e não há desfazer. Diz o que será
     * perdido antes de perder.
     */
    copyConfirm: {
      title: 'Isto substitui as alocações desta competência',
      description:
        'Copiar o mês anterior apaga todas as alocações já lançadas em {{competencia}} para ' +
        'esta empresa e coloca no lugar as do mês anterior. As linhas substituídas não podem ' +
        'ser recuperadas — só digitadas de novo.',
      confirm: 'Substituir pelas do mês anterior',
      cancel: 'Cancelar',
      success: 'Alocações do mês anterior copiadas para esta competência.',
    },
    save: 'Salvar',
    saving: 'Salvando…',
    // Nomeia a pessoa: numa grade de quarenta linhas quase idênticas, "Alocações salvas" não
    // diz qual linha foi gravada. Nenhuma cifra aqui — a grade inteira existe para não ter.
    saveSuccess: 'Alocações de {{person}} salvas.',
    overLimit: 'Acima de 100%',
    cellLabel: '{{person}} — percentual no projeto {{project}}',
    periodPosted:
      'A folha desta competência já foi contabilizada. Salvar uma alocação agora gera um ' +
      'lançamento de reclassificação no período aberto atual — a folha em si não é reaberta.',
    peopleTruncated:
      'A grade está mostrando as primeiras {{limit}} pessoas, em ordem alfabética. Há mais ' +
      'pessoas nesta competência — filtre por empresa para chegar a quem falta.',
    confidentialityNote:
      'A grade mostra apenas nomes e percentuais. Nenhum valor de remuneração aparece aqui: o ' +
      'custo é apurado no fechamento da folha e só é exibido como total do projeto.',
    columns: {
      person: 'Pessoa',
      regime: 'Regime',
      total: 'Total',
    },
    regime: {
      EMPLOYEE: 'CLT',
      COLLABORATOR: 'PJ',
    },
    empty: {
      noProjects: {
        title: 'Nenhum projeto aberto',
        description:
          'Não há projeto de intangível em pesquisa ou desenvolvimento para alocar pessoas. ' +
          'Crie um projeto na aba Projetos antes de distribuir percentuais.',
      },
      noPeople: {
        title: 'Nenhuma pessoa para alocar',
        description:
          'Não há colaboradores CLT ativos nem colaboradores PJ mapeados nesta competência.',
      },
    },
    /**
     * People cost hidden by the DEC-5 headcount floor. Deliberately says "confidencial" and
     * not "sem custo": the two are different facts, and the second would be a lie.
     */
    peopleCostSuppressed: 'Custo de pessoal: confidencial',
    /** The reclass confirmation. Names the consequence before it happens, not after. */
    correction: {
      // "Reclassificação", not "estorno": an estorno cancels a lançamento, and this cancels
      // nothing — the folha's journal stands untouched and a second journal moves the
      // difference between two accounts. Calling it an estorno told the accountant the folha
      // was being reversed, which is the one thing ADR 0017 D4 promises never happens.
      title: 'Isto lança uma reclassificação contábil',
      description:
        'A folha de {{competencia}} já foi contabilizada, então esta alteração não entra ' +
        'naquele lançamento: ela vira uma reclassificação com data de hoje, no período aberto ' +
        'atual, pela diferença entre a alocação anterior e a nova. A folha não é reaberta.',
      confirm: 'Lançar a reclassificação',
      cancel: 'Cancelar',
      // Confirma o que aconteceu, não o que vai acontecer: a reclassificação já está lançada
      // quando esta frase aparece.
      success:
        'Reclassificação lançada no período aberto atual. A folha da competência editada não ' +
        'foi reaberta.',
    },
    saveError: 'Não foi possível salvar esta linha.',
    copyError: 'Não foi possível copiar o mês anterior.',
    retry: 'Tentar de novo',
  },
  /**
   * The Intangíveis page (ADR 0017 D1/D12, slice ALC-3491).
   *
   * **"confidencial" is a first-class value here, not an error string.** Every money field on
   * this page arrives `string | null`, and null means the DEC-5 headcount floor withheld it —
   * not that the amount is zero. Rendering `R$ 0,00` for a withheld total would be a lie about
   * the balance sheet AND would leak the withheld figure the moment the reader subtracts it
   * from a visible one. Every place a total can be withheld has a `confidential` string here.
   */
  page: {
    title: 'Intangíveis',
    tabs: {
      projects: 'Projetos',
      assets: 'Ativos intangíveis',
      allocations: 'Alocações',
    },
    /** Shown wherever a total was withheld by the headcount floor (DEC-5). */
    confidential: 'confidencial',
    confidentialHint:
      'O custo de pessoal deste projeto está abaixo do piso de sigilo, então os totais são ' +
      'omitidos. O lançamento contábil carrega o valor real.',
    entityFilter: 'Empresa',
    statusFilter: 'Situação',
    classFilter: 'Classe',
    allEntities: 'Todas as empresas',
    allStatuses: 'Todas as situações',
    allClasses: 'Todas as classes',
    searchPlaceholder: 'Buscar por código ou nome',
    clearFilters: 'Limpar filtros',
    loadError: 'Não foi possível carregar os projetos de intangível.',
    loading: 'Carregando…',
    retry: 'Tentar de novo',
    status: {
      RESEARCH: 'Pesquisa',
      DEVELOPMENT: 'Desenvolvimento',
      IN_SERVICE: 'Em uso',
      WRITTEN_OFF: 'Baixado',
    },
    summary: {
      inProgress: 'Capitalizado em andamento',
      monthToDate: 'Capitalizado no mês',
      openProjects: 'Projetos em desenvolvimento',
      projectCount_one: '{{count}} projeto',
      projectCount_other: '{{count}} projetos',
      /**
       * A card whose sum lost a suppressed contributor. Says so instead of printing a total
       * that silently excludes a project — a reader comparing it against the razão would
       * otherwise find a difference with nothing explaining it.
       */
      partial: 'parcial — um projeto tem custo confidencial',
      /**
       * A lista veio truncada pelo teto do endpoint, então TODO card é subtotal — inclusive
       * um em que nenhum projeto foi suprimido. Os que caem fora são os mais antigos, que são
       * justamente os de saldo maior.
       */
      partialTruncated: 'parcial — há mais projetos do que esta lista comporta',
      /** Há contribuintes, nenhum foi suprimido, e mesmo assim não veio valor. */
      unavailable: '—',
    },
    columns: {
      code: 'Código',
      name: 'Projeto',
      class: 'Classe',
      entity: 'Empresa',
      status: 'Situação',
      gateDate: 'Início do desenvolvimento',
      capitalised: 'Capitalizado',
      monthToDate: 'No mês',
      actions: 'Ações',
    },
    noGateDate: 'Gate não aberto',
    /** Accessible name of the row's "Detalhes" link: keeps the visible word (WCAG 2.5.3). */
    projectDetails: 'Detalhes do projeto {{code}}',
    details: 'Detalhes',
    /** ADR 0017 D12 — the copy is the decision, quoted. */
    empty: {
      title: 'Nenhum projeto de intangível',
      description:
        'Crie um projeto quando a empresa desenvolver software ou conteúdo próprio. Licenças ' +
        'e marcas compradas entram pela fila de revisão.',
      action: 'Novo projeto',
    },
    emptyFiltered: {
      title: 'Nenhum projeto com estes filtros',
      description: 'Limpe os filtros para ver todos os projetos da empresa.',
    },
    create: {
      trigger: 'Novo projeto',
      title: 'Novo projeto de intangível',
      description:
        'O projeto nasce em Pesquisa: tudo que for apontado para ele é despesa até o gate do ' +
        'CPC 04 §57 ser aberto.',
      entity: 'Empresa',
      entityHint: 'A empresa que desenvolve. O custo capitaliza no balanço dela e em nenhum outro.',
      class: 'Classe de intangível',
      classHint: 'Somente classes do grupo Intangível. É ela que decide a conta e a vida útil.',
      classEmpty: 'Esta empresa não tem nenhuma classe de intangível ativa.',
      code: 'Código',
      codePlaceholder: 'CURSO-PY-V1',
      name: 'Nome',
      namePlaceholder: 'Curso de Python — versão 1',
      descriptionField: 'Descrição',
      art42: {
        label: 'Excluir o desenvolvimento da base fiscal (art. 42, Lei 12.973/2014)',
        inheritedOn: 'A empresa responde hoje: sim, aplica a exclusão.',
        inheritedOff: 'A empresa responde hoje: não aplica a exclusão.',
        override: 'Responder diferente para este projeto',
        /**
         * DEC-7: the answer is frozen at creation. Saying so in the dialog is what keeps an
         * accountant from changing the entity default later expecting this project to follow.
         */
        frozenHint:
          'A resposta é congelada na criação: mudar a resposta da empresa depois não altera ' +
          'este projeto.',
        noFiscalBook:
          'Esta empresa não tem livro fiscal (TAX_BASIS) ativo, então não há base fiscal onde ' +
          'aplicar a exclusão do art. 42. O campo não se aplica aqui.',
      },
      submit: 'Criar projeto',
      submitting: 'Criando…',
      success: 'Projeto {{code}} criado.',
      cancel: 'Cancelar',
    },
    gate: {
      trigger: 'Abrir o gate',
      title: 'Abrir o gate de capitalização (CPC 04 §57)',
      description:
        'As seis condições precisam ser atendidas ao mesmo tempo. A partir da data informada, ' +
        'o custo do projeto passa a capitalizar; o custo anterior permanece em despesa.',
      date: 'Início do desenvolvimento',
      reason: 'Motivo',
      reasonHint:
        'Obrigatório ao alterar uma data já certificada: é o que explica, na auditoria, por ' +
        'que a competência a partir da qual o custo capitaliza mudou.',
      checklistTitle: 'Condições do CPC 04 §57',
      checklistIncomplete: 'Marque todas as condições para abrir o gate.',
      templateMissing:
        'Não há checklist cadastrado para o framework contábil deste projeto, então o gate ' +
        'não pode ser aberto.',
      history: 'Certificações anteriores',
      supersededOn: 'Substituída em {{date}}',
      submit: 'Certificar e abrir o gate',
      submitting: 'Certificando…',
      success: 'Gate aberto. O projeto está em Desenvolvimento.',
      cancel: 'Cancelar',
    },
    goLive: {
      trigger: 'Colocar em uso',
      title: 'Colocar o projeto em uso',
      description:
        'Um lançamento de reclassificação move o saldo capitalizado para a conta da classe e ' +
        'o projeto vira um ativo. O projeto fecha: trabalho posterior é despesa (CPC 04 §20).',
      date: 'Data de entrada em uso',
      dateHint:
        'É também a data do lançamento, então precisa cair em um período aberto, e não pode ' +
        'ser anterior à última competência que capitalizou custo.',
      usefulLife: 'Vida útil (meses)',
      usefulLifeFromClass: 'A régua da classe é de {{months}} meses.',
      usefulLifeUnknown: 'A classe não tem régua societária cadastrada.',
      overrideReason: 'Motivo da vida útil diferente',
      overrideReasonHint:
        'Obrigatório quando a vida útil difere da régua da classe. Fica registrado no bem, ' +
        'como resposta a “por que a régua deste bem não é a da classe”.',
      dimensionsTitle: 'Dimensões do ativo',
      dimensionsHint:
        'A amortização de toda a vida útil é lançada nestas dimensões. Deixe em branco para ' +
        'herdar a dos custos do projeto — obrigatório escolher quando eles vieram de mais de uma.',
      costCenter: 'Centro de custo',
      department: 'Departamento',
      managerialAccount: 'Conta gerencial',
      inheritPlaceholder: 'Herdar dos custos',
      submit: 'Colocar em uso',
      submitting: 'Colocando em uso…',
      success: 'Projeto em uso. Ativo {{code}} criado.',
      successConfidential:
        'Projeto em uso. Ativo {{code}} criado. O valor capitalizado é confidencial nesta tela.',
      cancel: 'Cancelar',
    },
    writeOff: {
      trigger: 'Baixar projeto',
      title: 'Baixar o projeto',
      description:
        'O saldo capitalizado vai para uma conta de perda e o projeto fecha. As linhas de ' +
        'custo não são apagadas nem estornadas: o dinheiro foi mesmo gasto, e o que a baixa ' +
        'registra é que ele nunca virou ativo. Não há como desfazer.',
      reason: 'Motivo da baixa',
      reasonHint:
        'Obrigatório. O desreconhecimento do CPC 04 §112 é um julgamento, e um saldo movido ' +
        'para perda sem motivo é inauditável.',
      date: 'Data do lançamento',
      submit: 'Baixar o projeto',
      submitting: 'Baixando…',
      success: 'Projeto baixado.',
      cancel: 'Cancelar',
    },
    detail: {
      notFound: 'Projeto não encontrado.',
      identificationTitle: 'Identificação',
      totalsTitle: 'Totais',
      createdAt: 'Criado em',
      description: 'Descrição',
      costTitle: 'Custo por competência',
      columns: {
        competencia: 'Competência',
        source: 'Origem',
      },
      /** The whole reason this drawer shows groups and not lines. */
      costNote:
        'Totais por competência e origem. Esta tela não mostra linha de custo nem pessoa: um ' +
        'custo de pessoal é a remuneração de alguém, e ver o balanço não dá direito a ela.',
      capitalised: 'Capitalizado',
      nonCapitalised: 'Não capitalizado',
      nonCapitalisedNote: 'Custo anterior ao gate, que foi para despesa (CPC 04 §57).',
      noCost: 'Nenhum custo apontado para este projeto ainda.',
      sourceType: {
        PAYROLL_ALLOCATION: 'Pessoal CLT',
        PJ_BILL_LINE: 'Pessoal PJ',
        AP_BILL_LINE: 'Fornecedores',
      },
      gateTitle: 'Gate de capitalização',
      gateOpenOn: 'Desenvolvimento desde {{date}}',
      gateClosed: 'Gate não aberto — todo o custo é despesa.',
      originAsset: 'Ativo gerado',
      openAsset: 'Abrir o ativo',
      writtenOffOn: 'Baixado em {{date}}',
      writeOffReason: 'Motivo da baixa',
    },
    registry: {
      /** The Ativos intangíveis tab — the registry, narrowed to the INTANGIBLE group. */
      description:
        'Os bens do grupo Intangível já em uso: licenças e marcas compradas, e os projetos ' +
        'que entraram em uso.',
      /**
       * A leitura da aba falhou. Distinta do estado vazio de propósito: dizer "nenhum ativo
       * intangível" quando a chamada caiu é dar uma resposta errada com cara de resposta certa.
       */
      loadError:
        'Não foi possível carregar os ativos intangíveis. A lista abaixo pode estar incompleta.',
      originProject: 'Origem: projeto {{code}}',
      empty: {
        title: 'Nenhum ativo intangível',
        description:
          'Licenças e marcas compradas entram por aqui pela fila de revisão; software e ' +
          'conteúdo próprios, pelo go-live de um projeto.',
      },
      /**
       * `GET /assets` has no group filter, so the tab reads one page per intangible class and
       * merges them. This says so when a class filled its page, rather than showing a total
       * that quietly stops counting.
       */
      truncated:
        'Mostrando os primeiros {{limit}} bens por classe. Filtre por classe para ver os que ' +
        'faltam.',
      columns: {
        code: 'Plaqueta',
        description: 'Descrição',
        class: 'Classe',
        entity: 'Empresa',
        inServiceDate: 'Em uso desde',
        cost: 'Custo',
      },
    },
    allocations: {
      /** The third tab. Hidden without `hcm:read` — see the controller's permission split. */
      description:
        'Distribua o mês de cada pessoa entre os projetos abertos da empresa selecionada. ' +
        'Percentuais apenas: nenhum valor de remuneração aparece nesta tela.',
      entityRequired: 'Selecione uma empresa para editar as alocações.',
      entityRequiredDescription:
        'As alocações são gravadas por empresa pagadora, então a grade só abre com uma ' +
        'empresa escolhida.',
      loadError: 'Não foi possível carregar a grade de alocações.',
    },
    /** Column-header filters, sorting and paging of the projects listing. */
    listing: {
      filtersActive: 'Filtros ativos',
      filterColumn: 'Filtrar por {{column}}',
      sortTooltip: 'Clique para alternar a ordem',
      sortBy: 'Ordenar por {{column}}',
      searchApply: 'Aplicar',
      /** The search matches code or name, so its chip says both. */
      searchChip: 'Código ou nome',
      pagination: '{{start}}–{{end}} de {{total}} projetos',
      selectPage: 'Selecionar todos os projetos desta página',
      selectRow: 'Selecionar o projeto {{code}}',
    },
    dialog: {
      close: 'Fechar',
      /** A refusal whose code has no copy of its own. Never the server's raw message. */
      genericRefusal: 'Não foi possível concluir a operação. Tente de novo.',
    },
    /**
     * Bulk lifecycle actions. Each one posts project by project and reports every outcome in
     * the dialog: a batch is never all-or-nothing, so a single summary toast would hide which
     * projects actually moved.
     */
    bulk: {
      gate: 'Abrir o gate',
      goLive: 'Colocar em uso',
      writeOff: 'Baixar projetos',
      ineligible: {
        gate: 'O gate só pode ser aberto em projetos em Pesquisa ou Desenvolvimento.',
        gateFramework:
          'Os projetos selecionados seguem frameworks contábeis diferentes, cada um com seu ' +
          'checklist. Abra o gate de um framework por vez.',
        goLive: 'Só projetos em Desenvolvimento podem ser colocados em uso.',
        writeOff: 'Só projetos em Pesquisa ou Desenvolvimento podem ser baixados.',
      },
      gateTitle_one: 'Abrir o gate de {{count}} projeto',
      gateTitle_other: 'Abrir o gate de {{count}} projetos',
      goLiveTitle_one: 'Colocar {{count}} projeto em uso',
      goLiveTitle_other: 'Colocar {{count}} projetos em uso',
      writeOffTitle_one: 'Baixar {{count}} projeto',
      writeOffTitle_other: 'Baixar {{count}} projetos',
      gateReasonHint:
        'Obrigatório porque ao menos um projeto já está em Desenvolvimento com outra data ' +
        'certificada.',
      goLiveLifeTitle: 'Vida útil por classe',
      goLiveLifeHint:
        'Cada projeto entra em uso com a vida útil da sua classe. Para usar outra vida útil, ' +
        'coloque o projeto em uso individualmente.',
      goLiveLifeFromClass: '{{className}}: {{months}} meses',
      goLiveLifeMissing:
        '{{className}} não tem régua societária cadastrada. Coloque esses projetos em uso ' +
        'individualmente.',
      submit_one: 'Aplicar a {{count}} projeto',
      submit_other: 'Aplicar a {{count}} projetos',
      running: 'Processando {{done}} de {{total}}…',
      resultSummary: '{{succeeded}} de {{total}} concluídos.',
      resultAllOk: 'Todos os projetos foram processados.',
      resultOk: 'Concluído',
      resultFailed: 'Falhou',
      retryFailed: 'Tentar de novo os que falharam',
    },
  },
} as const;

export const assetsIntangibleEn = {
  errors: {
    INTANGIBLE_PROJECT_NOT_FOUND: 'Intangible project not found.',
    INTANGIBLE_PROJECT_ENTITY_NOT_FOUND: 'Entity not found.',
    INTANGIBLE_PROJECT_CLASS_NOT_FOUND: 'Asset class not found, or not active.',
    INTANGIBLE_PROJECT_CLASS_NOT_INTANGIBLE:
      'This class is not in the Intangible group. A development project capitalises cost under ' +
      'CPC 04, so it must point at an intangible class.',
    INTANGIBLE_PROJECT_STATUTORY_BOOK_REQUIRED:
      'This entity has no active book under the chosen class’s framework. Configure the book ' +
      'before opening an intangible development project.',
    INTANGIBLE_PROJECT_FRAMEWORK_NOT_ON_ENTITY:
      'This class belongs to an accounting framework the entity keeps no active book under. A ' +
      'project measured under a standard the entity does not report in would certify the ' +
      'checklist conditions against the wrong one.',
    INTANGIBLE_PROJECT_CODE_DUPLICATE:
      'A project already uses this code. Choose another identifier.',
    INTANGIBLE_FISCAL_BOOK_AMBIGUOUS:
      'This entity has more than one active fiscal (TAX_BASIS) book in its measurement scope, ' +
      'and the art. 42 exclusion needs exactly one. Open Settings → Accounting & Tax → Books, ' +
      'leave only one of them active for this entity, then post again.',
    INTANGIBLE_FISCAL_BOOK_MISSING_AFTER_EXCLUSION:
      'This project has already excluded cost from taxable profit under Lei 12.973 art. 42, ' +
      'but this entity no longer has an active fiscal (TAX_BASIS) book in its measurement ' +
      'scope. Without the book, the addition that gives that exclusion back has nowhere to be ' +
      'recorded, and the same cost would be deducted twice. Open Settings → Accounting & Tax → ' +
      'Books, re-activate this entity’s fiscal book (or put the entity back in its scope), then ' +
      'try again.',
    INTANGIBLE_PROJECT_REVERSAL_AFTER_CLOSE:
      'This project has already left research/development, so its cost can no longer be ' +
      'reversed through the source document. The art. 42 exclusion would be retracted, but the ' +
      'asset’s fiscal base was frozen at go-live and is not recomputed — the additions would ' +
      'carry on at the old figure and the temporary difference would become permanent, with ' +
      'tax overpaid for good. Correct it on the asset instead (disposal or revision), which ' +
      'adjusts both sides; if the document really must be reversed, that has to happen before ' +
      'the project goes live.',
    INTANGIBLE_PROJECT_CLOSED:
      'This project has gone live or been written off and takes no further changes. Work after ' +
      'go-live is expense (CPC 04 §20); a new version is a new project.',
    INTANGIBLE_PROJECT_DATE_INVALID: 'Enter a valid date in YYYY-MM-DD format.',
    INTANGIBLE_GATE_TEMPLATE_MISSING:
      'No capitalisation checklist is registered for this project’s accounting framework. ' +
      'Without the standard’s conditions the development gate cannot be opened.',
    INTANGIBLE_GATE_INCOMPLETE:
      'All six CPC 04 §57 conditions must hold before development cost may be capitalised. ' +
      'While any is missing, the project’s cost stays in expense.',
    INTANGIBLE_GATE_ITEM_UNKNOWN:
      'One of the conditions sent does not belong to this project’s framework checklist.',
    INTANGIBLE_GATE_NOT_RETROACTIVE:
      'The development start date can only be moved later, never earlier: capitalisation runs ' +
      'from that period forward, and moving the date back would ask closed periods to ' +
      'reclassify expense they already reported.',
    INTANGIBLE_GATE_BEFORE_PROJECT_START:
      'The development start date cannot precede the period the project was created in — no ' +
      'cost was tracked before then.',
    INTANGIBLE_GATE_RECERTIFICATION_REASON_REQUIRED:
      'Give a reason for changing the development start date. The previous certification is ' +
      'kept, and the reason is what explains, on audit, why the period cost capitalises from ' +
      'has moved.',
    INTANGIBLE_ENTITY_POLICY_NO_FISCAL_BOOK:
      'This entity has no active fiscal (TAX_BASIS) book. The Lei 12.973/2014 art. 42 ' +
      'exclusion is recorded in the fiscal book, so there is nothing here to apply it to.',
    INTANGIBLE_ENTITY_POLICY_NOT_RETROACTIVE:
      'An art. 42 policy change takes effect when it is made and cannot be backdated: projects ' +
      'created under the previous answer have already frozen their election, and moving it now ' +
      'would rewrite past decisions.',
    INTANGIBLE_IN_PROGRESS_ACCOUNT_UNRESOLVED:
      'The Intangible in progress slot is not mapped for this chart-of-accounts version, or it ' +
      'points at an account that is not an active, postable, non-contra debit asset. Fix the ' +
      'mapping before capitalising development cost into it.',

    // ── Review queue: the ADD_TO_PROJECT verdict (ALC-3487, ADR 0017 D3) ─────────────────
    INTANGIBLE_PROJECT_REQUIRED:
      'Name the target project to route this line to a development project.',
    INTANGIBLE_PROJECT_NOT_OPEN:
      'This project has gone live or been written off and takes no further cost. Work after ' +
      'go-live is expense (CPC 04 §20); a new version is a new project.',
    INTANGIBLE_PROJECT_ENTITY_MISMATCH:
      'The project belongs to a different entity than the bill — or, on an allocation, than the ' +
      'entity employing the person. A project’s cost belongs to the entity that develops it, ' +
      'and someone’s cost only capitalises on the balance sheet of the entity paying them; ' +
      'linking the two would move value between entities with no intercompany entry.',
    INTANGIBLE_PROJECT_BILL_ALREADY_POSTED:
      'This bill has already been posted, so linking it to a project now would need a ' +
      'reclassification journal. Send the bill back for approval (which reverses its ' +
      'posting), decide this candidate, then approve the bill again.',
    INTANGIBLE_PROJECT_CLASSIFICATION_MULTIPLE_PROJECTS:
      'One classification routes lines to more than one project. The journal line carries the ' +
      'classification and nothing finer, so the whole debit would land on a single project. ' +
      'Classify the bill per line.',
    INTANGIBLE_PROJECT_BOOK_NOT_POSTED:
      'The bill posted no journal in the book of the framework the project is measured under, ' +
      'so the project’s cost would have nothing in the ledger to reconcile against.',
    INTANGIBLE_PROJECT_FRAMEWORK_NOT_POSTED:
      'The project is measured in a framework this entity keeps only as a memo book. The bill ' +
      'provision posts only to the posted book, so there is nowhere to record the cost. Use a ' +
      "project measured in the entity's posted-book framework.",
    INTANGIBLE_PROJECT_FRAMEWORK_MULTIPLE_BOOKS:
      'The entity has more than one posted book under the framework the project is measured ' +
      'under, and the project’s cost has to point at a single ledger. Fix the frameworks on the ' +
      'entity’s books before posting.',

    // ── Project go-live and write-off (ALC-3489, ADR 0017 D6, D7, D8) ───────────────────
    INTANGIBLE_PROJECT_NO_CAPITALISED_COST:
      'This project has no capitalised cost. Either the CPC 04 §57 gate was never opened, or ' +
      'every expense predates it and stays in the income statement — neither leaves an asset to ' +
      'recognise. Open the gate and capitalise at least one competência before putting the ' +
      'project into service, or write the project off.',
    INTANGIBLE_PROJECT_GO_LIVE_BEFORE_LAST_COST:
      'The in-service date precedes the last competência that capitalised cost. The asset is ' +
      'born with the whole capitalised total and amortises from its in-service date, so putting ' +
      'it into service before the month of its own cost would amortise a base the ledger had ' +
      'not yet assembled.',
    INTANGIBLE_PROJECT_BALANCE_MISMATCH:
      'The ledger balance of Intangibles in progress for this project does not match the ' +
      'capitalised cost recorded on the project. While the two disagree, closing the project ' +
      'would post the difference against a project that ceases to exist — and only a manual ' +
      'entry could clear it afterwards. Reconcile the account before go-live or write-off.',
    INTANGIBLE_PROJECT_GO_LIVE_DIMENSION_AMBIGUOUS:
      'This project’s costs were posted to more than one cost centre, department or ' +
      'managerial account. Put the project into service on its own and choose which one the ' +
      'asset amortises under — every month of its useful life follows the asset’s dimensions.',
    INTANGIBLE_PROJECT_GO_LIVE_DIMENSION_WRONG_ENTITY:
      'The chosen dimension does not belong to the project’s entity. Choose a cost centre, ' +
      'department or managerial account of the same entity.',
    INTANGIBLE_BOOK_DATE_AMBIGUOUS:
      'This entity’s books are in different time zones and, right now, on different days. ' +
      'Enter the posting date.',
    INTANGIBLE_PROJECT_LIFE_OVERRIDE_REASON_REQUIRED:
      'Give a reason for using a useful life different from the class rule. The life decides ' +
      'amortisation for every later month (CPC 04 §97), so diverging from the class rule is a ' +
      'judgement an auditor has to be able to read on the asset record.',
    INTANGIBLE_PROJECT_WRITE_OFF_REASON_REQUIRED:
      'Give a reason for the write-off. Derecognising a development project is a judgement ' +
      '(CPC 04 §112), not a routine entry: a balance moved to a loss account with nothing ' +
      'saying why is unauditable.',
    INTANGIBLE_PROJECT_WRITE_OFF_BEFORE_LAST_COST:
      'The write-off date precedes the last competência that capitalised cost. The write-off ' +
      'credits the project’s whole Intangibles-in-progress balance in whichever competência it ' +
      'is posted to, and in that competência the ledger had not yet received all of it — the ' +
      'account would close the month negative. The write-off belongs to the current period ' +
      '(CPC 04 §112).',
    INTANGIBLE_PROJECT_ASSET_BOOK_RULE_MISSING:
      'The project’s class has no amortisation rule on the book that receives the go-live ' +
      'reclassification. Without one the asset would go into service with no statutory ' +
      'amortisation. Add the class rule for that book before placing the project in service.',
    INTANGIBLE_PROJECT_BOOK_CURRENCY_CONFLICT:
      'The books that account for this project’s framework have different functional ' +
      'currencies. One project becomes exactly one asset, and an asset has a single cost in a ' +
      'single currency — it cannot be recorded in two. Unify the books’ functional currency ' +
      'before putting the project into service.',
    INTANGIBLE_WRITE_OFF_LOSS_ACCOUNT_UNRESOLVED:
      'The Intangible project write-off loss slot is not mapped for this chart-of-accounts ' +
      'version. Fix the mapping before writing off a development project.',
    INTANGIBLE_WRITE_OFF_LOSS_ACCOUNT_TYPE_INVALID:
      'The Intangible project write-off loss slot points at an account that is not an active, ' +
      'postable, non-contra debit expense account (Expense or Other expense). Abandoning a ' +
      'project is always a loss in the result (CPC 04 §112); fix the mapping.',
    ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_INTANGIBLE:
      'Indefinite useful life exists only for classes in the Intangible group (CPC 04 §107). ' +
      'Property, plant and equipment and right-of-use assets are always depreciated or ' +
      'amortised; adjust the class rule instead of flagging the life as indefinite.',
    ASSET_CLASS_INDEFINITE_LIFE_REQUIRES_NONE:
      'An indefinite-life intangible is not amortised (CPC 04 §107) — it is tested for ' +
      'impairment once a year instead. The class’s statutory rule has to use method NONE: ' +
      'flagging the class without stopping the schedule would leave the disclosure saying one ' +
      'thing and the measurement doing another.',
    INTANGIBLE_PROJECT_CLASSIFICATION_COVERS_UNROUTED_LINES:
      'The classification that routes cost to the project also covers lines on this bill that ' +
      'were not routed to it, and the whole classification’s debit would go to intangibles in ' +
      'progress. Classify the bill per line before posting.',
    INTANGIBLE_PROJECT_PAYROLL_BILL_NOT_ROUTABLE:
      'This bill is not routed to projects from the review queue: it is either a payroll bill ' +
      'or a mapped PJ contractor’s monthly bill. That cost reaches a project through the ' +
      'project’s own standing allocation — routing it here would count the same cost twice.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_DEFERRAL:
      'This bill is routed to a development project and also has an active deferral. Cost that ' +
      'builds an asset is capitalised into the project and amortised from go-live — cancel one ' +
      'before posting.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_CLIENT_REIMBURSABLE:
      'This bill is client-reimbursable and also routed to a development project. Cost ' +
      'recharged to a client is not the company’s own development cost.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_CAPITALIZATION:
      'One classification on this bill both capitalises an asset and routes cost to a ' +
      'development project. A cost is either an asset of its own or one month of a project, never ' +
      'both — classify the bill per line so each decision reaches its own account.',
    BILL_INTANGIBLE_PROJECT_CONFLICTS_WITH_LEASE_SETTLEMENT:
      'This bill settles a lease instalment and is also routed to a development project. An ' +
      'instalment liquidates an obligation already on the balance sheet; it does not build a ' +
      'new asset.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_DEFERRAL:
      'This bill is a PJ collaborator’s with an allocation split for the period and also has an ' +
      'active deferral. The split would take a fraction of a debit that is not yet the period’s ' +
      'expense — cancel the deferral or clear the period’s allocation.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_CLIENT_REIMBURSABLE:
      'This bill is client-reimbursable and also a PJ collaborator’s with an allocation split ' +
      'for the period. Cost recharged to a client is not the company’s own development cost — ' +
      'unmark the recharge or clear the period’s allocation.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_LEASE_SETTLEMENT:
      'This bill settles a lease instalment and is also a PJ collaborator’s with an allocation ' +
      'split for the period. An instalment liquidates an obligation already on the balance ' +
      'sheet; it does not build a new asset.',
    BILL_INTANGIBLE_COLLABORATOR_SPLIT_CONFLICTS_WITH_CAPITALIZATION:
      'One classification on this bill both capitalises an asset and feeds the PJ ' +
      'collaborator’s allocation split. The split would send a fraction of the asset’s own cost ' +
      'to intangibles in progress — classify the bill per line, or cancel the capitalisation.',

    INTANGIBLE_ALLOCATION_COMPETENCIA_INVALID:
      'The period must be in YYYY-MM format, for example 2026-06.',
    INTANGIBLE_ALLOCATION_PERIOD_POSTED:
      'This period’s payroll has already been posted. Saving the allocation now produces a ' +
      'reclassification journal in the current open period — confirm to continue. The payroll ' +
      'run itself is not reopened.',
    INTANGIBLE_ALLOCATION_CORRECTION_REQUIRES_GL:
      'This period’s payroll has already been posted, so correcting the allocation now posts a ' +
      'journal. Ask someone with accounting permission to save this correction.',
    INTANGIBLE_ALLOCATION_PROJECT_DUPLICATE:
      'The same project appears twice in this person’s row. Combine the percentages into one ' +
      'cell.',
    INTANGIBLE_ALLOCATION_PERCENT_INVALID:
      'Each percentage must be above zero and at most 100. To take someone off a project, ' +
      'clear the cell rather than typing zero.',
    INTANGIBLE_ALLOCATION_OVER_100:
      'This person’s percentages for the period add up to more than 100%. Nobody can be ' +
      'allocated beyond their own cost.',
    INTANGIBLE_ALLOCATION_PERSON_NOT_FOUND:
      'This person was not found, or is no longer active. Reload the grid.',
    INTANGIBLE_ALLOCATION_CORRECTION_NOT_SUPPORTED_FOR_PJ:
      'PJ collaborator allocations produce no reclassification journal: their cost arrives as ' +
      'an invoice. Reclassify the bill in accounts payable instead.',
    INTANGIBLE_ALLOCATION_ENTITY_MISMATCH:
      'The selected company does not employ this person. A CLT employee’s cost capitalises ' +
      'only on the balance sheet of the company that pays them: switch the grid to their ' +
      'employer before allocating.',
    INTANGIBLE_ALLOCATION_CORRECTION_BASE_UNRESOLVED:
      'The expense account that received this person’s cost for the period could not be ' +
      'identified. Check the payroll dimension mapping and the payroll account mapping ' +
      '(salaries, charges and provisions) before correcting the allocation.',
    GL_BOOK_CURRENCY_UNSUPPORTED:
      'This entity’s book has no supported functional currency configured, so the allocation ' +
      'reclassification cannot be posted.',
    JOURNAL_NO_OPEN_PERIOD:
      'No open accounting period can receive the allocation reclassification. The period’s ' +
      'payroll is not reopened — open the current period and save again.',
  },
  allocations: {
    loading: 'Loading allocations…',
    competenciaLabel: 'Period',
    copyPrevious: 'Copy previous month',
    copying: 'Copying…',
    copySuccess: 'Previous month copied.',
    copyConfirm: {
      title: 'This replaces the allocations for this period',
      description:
        'Copying the previous month deletes every allocation already entered for ' +
        '{{competencia}} for this entity and puts the previous month’s in their place. The ' +
        'replaced rows cannot be recovered — only typed again.',
      confirm: 'Replace with the previous month',
      cancel: 'Cancel',
      success: 'The previous month’s allocations were copied into this period.',
    },
    save: 'Save',
    saving: 'Saving…',
    saveSuccess: 'Allocations saved for {{person}}.',
    overLimit: 'Above 100%',
    cellLabel: '{{person}} — percentage on project {{project}}',
    periodPosted:
      'This period’s payroll has already been posted. Saving an allocation now produces a ' +
      'reclassification journal in the current open period — the payroll run itself is never ' +
      'reopened.',
    peopleTruncated:
      'Showing the first {{limit}} people, in alphabetical order. More people match this ' +
      'period — filter by entity to reach the rest.',
    confidentialityNote:
      'The grid shows names and percentages only. No pay figure appears here: cost is resolved ' +
      'when payroll posts and is shown only as a project total.',
    columns: {
      person: 'Person',
      regime: 'Regime',
      total: 'Total',
    },
    regime: {
      EMPLOYEE: 'Employee',
      COLLABORATOR: 'Contractor',
    },
    empty: {
      noProjects: {
        title: 'No open project',
        description:
          'There is no intangible project in research or development to allocate people to. ' +
          'Create one on the Projects tab before distributing percentages.',
      },
      noPeople: {
        title: 'Nobody to allocate',
        description: 'There are no active employees and no mapped contractors for this period.',
      },
    },
    peopleCostSuppressed: 'People cost: confidential',
    correction: {
      title: 'This posts a reclassification journal',
      description:
        'Payroll for {{competencia}} has already been posted, so this change does not go into ' +
        'that journal: it becomes a reclassification dated today, in the current open period, ' +
        'for the difference between the old allocation and the new one. The payroll run is ' +
        'not reopened.',
      confirm: 'Post the reclassification',
      cancel: 'Cancel',
      success:
        'Reclassification posted in the current open period. The payroll run for the period ' +
        'you edited was not reopened.',
    },
    saveError: 'This row could not be saved.',
    copyError: 'The previous month could not be copied.',
    retry: 'Try again',
  },
  page: {
    title: 'Intangibles',
    tabs: {
      projects: 'Projects',
      assets: 'Intangible assets',
      allocations: 'Allocations',
    },
    confidential: 'confidential',
    confidentialHint:
      'This project’s people cost is below the confidentiality floor, so its totals are ' +
      'withheld. The journal carries the real figure.',
    entityFilter: 'Entity',
    statusFilter: 'Status',
    classFilter: 'Class',
    allEntities: 'All entities',
    allStatuses: 'All statuses',
    allClasses: 'All classes',
    searchPlaceholder: 'Search by code or name',
    clearFilters: 'Clear filters',
    loadError: 'The intangible projects could not be loaded.',
    loading: 'Loading…',
    retry: 'Try again',
    status: {
      RESEARCH: 'Research',
      DEVELOPMENT: 'Development',
      IN_SERVICE: 'In service',
      WRITTEN_OFF: 'Written off',
    },
    summary: {
      inProgress: 'Capitalised in progress',
      monthToDate: 'Capitalised this month',
      openProjects: 'Projects in development',
      projectCount_one: '{{count}} project',
      projectCount_other: '{{count}} projects',
      partial: 'partial — one project’s cost is confidential',
      partialTruncated: 'partial — there are more projects than this list holds',
      unavailable: '—',
    },
    columns: {
      code: 'Code',
      name: 'Project',
      class: 'Class',
      entity: 'Entity',
      status: 'Status',
      gateDate: 'Development started',
      capitalised: 'Capitalised',
      monthToDate: 'This month',
      actions: 'Actions',
    },
    noGateDate: 'Gate not opened',
    projectDetails: 'Details for project {{code}}',
    details: 'Details',
    empty: {
      title: 'No intangible projects',
      description:
        'Create a project when the company develops its own software or content. Bought ' +
        'licences and brands come in through the review queue.',
      action: 'New project',
    },
    emptyFiltered: {
      title: 'No projects match these filters',
      description: 'Clear the filters to see every project in the company.',
    },
    create: {
      trigger: 'New project',
      title: 'New intangible project',
      description:
        'The project is born in Research: everything charged to it is expense until the CPC ' +
        '04 §57 gate opens.',
      entity: 'Entity',
      entityHint:
        'The company doing the development. The cost capitalises on its balance sheet and on ' +
        'no other.',
      class: 'Intangible class',
      classHint:
        'Intangible-group classes only. The class decides the account and the useful life.',
      classEmpty: 'This entity has no active intangible class.',
      code: 'Code',
      codePlaceholder: 'CURSO-PY-V1',
      name: 'Name',
      namePlaceholder: 'Python course — version 1',
      descriptionField: 'Description',
      art42: {
        label: 'Exclude the development from the tax base (art. 42, Law 12.973/2014)',
        inheritedOn: 'The entity answers today: yes, it takes the exclusion.',
        inheritedOff: 'The entity answers today: no, it does not take the exclusion.',
        override: 'Answer differently for this project',
        frozenHint:
          'The answer is frozen at creation: changing the entity answer later does not reach ' +
          'back into this project.',
        noFiscalBook:
          'This entity has no active tax book (TAX_BASIS), so there is no tax base to apply ' +
          'the art. 42 exclusion to. The field does not apply here.',
      },
      submit: 'Create project',
      submitting: 'Creating…',
      success: 'Project {{code}} created.',
      cancel: 'Cancel',
    },
    gate: {
      trigger: 'Open the gate',
      title: 'Open the capitalisation gate (CPC 04 §57)',
      description:
        'All six conditions must hold at once. From the date you give, the project’s cost ' +
        'starts capitalising; the cost before it stays in expense.',
      date: 'Development started on',
      reason: 'Reason',
      reasonHint:
        'Required when changing a date that was already certified: it is what explains, in an ' +
        'audit, why the period cost starts capitalising in has moved.',
      checklistTitle: 'CPC 04 §57 conditions',
      checklistIncomplete: 'Tick every condition to open the gate.',
      templateMissing:
        'There is no checklist registered for this project’s accounting framework, so the ' +
        'gate cannot be opened.',
      history: 'Earlier certifications',
      supersededOn: 'Superseded on {{date}}',
      submit: 'Certify and open the gate',
      submitting: 'Certifying…',
      success: 'Gate opened. The project is in Development.',
      cancel: 'Cancel',
    },
    goLive: {
      trigger: 'Put into service',
      title: 'Put the project into service',
      description:
        'One reclassification journal moves the capitalised balance into the class’s own ' +
        'account and the project becomes an asset. The project closes: work after this is ' +
        'expense (CPC 04 §20).',
      date: 'In-service date',
      dateHint:
        'This is also the journal date, so it must fall in an open period, and it may not ' +
        'precede the last period that capitalised cost.',
      usefulLife: 'Useful life (months)',
      usefulLifeFromClass: 'The class schedule is {{months}} months.',
      usefulLifeUnknown: 'The class has no statutory schedule registered.',
      overrideReason: 'Reason for the different useful life',
      overrideReasonHint:
        'Required whenever the useful life differs from the class schedule. It is stored on ' +
        'the asset, as the answer to “why is this asset’s schedule not the class’s”.',
      dimensionsTitle: 'Asset dimensions',
      dimensionsHint:
        'Every month of amortisation is posted on these dimensions. Leave blank to inherit ' +
        'the project costs’ own — a choice is required when they came from more than one.',
      costCenter: 'Cost centre',
      department: 'Department',
      managerialAccount: 'Managerial account',
      inheritPlaceholder: 'Inherit from costs',
      submit: 'Put into service',
      submitting: 'Putting into service…',
      success: 'Project in service. Asset {{code}} created.',
      successConfidential:
        'Project in service. Asset {{code}} created. The capitalised amount is confidential ' +
        'on this screen.',
      cancel: 'Cancel',
    },
    writeOff: {
      trigger: 'Write off project',
      title: 'Write off the project',
      description:
        'The capitalised balance goes to a loss account and the project closes. The cost ' +
        'lines are neither deleted nor reversed: the money was really spent, and what the ' +
        'write-off records is that it never became an asset. This cannot be undone.',
      reason: 'Write-off reason',
      reasonHint:
        'Required. CPC 04 §112 derecognition is a judgement, and a balance moved to a loss ' +
        'account with nothing saying why is unauditable.',
      date: 'Journal date',
      submit: 'Write off the project',
      submitting: 'Writing off…',
      success: 'Project written off.',
      cancel: 'Cancel',
    },
    detail: {
      notFound: 'Project not found.',
      identificationTitle: 'Identification',
      totalsTitle: 'Totals',
      createdAt: 'Created on',
      description: 'Description',
      costTitle: 'Cost by period',
      columns: {
        competencia: 'Period',
        source: 'Source',
      },
      costNote:
        'Totals by period and source. This screen shows no cost line and no person: a people ' +
        'cost is somebody’s pay, and reading the balance sheet is not a right to it.',
      capitalised: 'Capitalised',
      nonCapitalised: 'Not capitalised',
      nonCapitalisedNote: 'Cost incurred before the gate, which went to expense (CPC 04 §57).',
      noCost: 'No cost has been charged to this project yet.',
      sourceType: {
        PAYROLL_ALLOCATION: 'People (employees)',
        PJ_BILL_LINE: 'People (contractors)',
        AP_BILL_LINE: 'Suppliers',
      },
      gateTitle: 'Capitalisation gate',
      gateOpenOn: 'In development since {{date}}',
      gateClosed: 'Gate not opened — all cost is expense.',
      originAsset: 'Resulting asset',
      openAsset: 'Open the asset',
      writtenOffOn: 'Written off on {{date}}',
      writeOffReason: 'Write-off reason',
    },
    registry: {
      description:
        'Intangible-group assets already in service: bought licences and brands, and the ' +
        'projects that went live.',
      loadError: 'The intangible assets could not be loaded. The list below may be incomplete.',
      originProject: 'Origin: project {{code}}',
      empty: {
        title: 'No intangible assets',
        description:
          'Bought licences and brands arrive through the review queue; own software and ' +
          'content, through a project go-live.',
      },
      truncated: 'Showing the first {{limit}} assets per class. Filter by class to reach the rest.',
      columns: {
        code: 'Tag',
        description: 'Description',
        class: 'Class',
        entity: 'Entity',
        inServiceDate: 'In service since',
        cost: 'Cost',
      },
    },
    allocations: {
      description:
        'Split each person’s month across the selected entity’s open projects. Percentages ' +
        'only: no pay amount appears on this screen.',
      entityRequired: 'Pick an entity to edit allocations.',
      entityRequiredDescription:
        'Allocations are saved per paying entity, so the grid only opens once one is picked.',
      loadError: 'The allocation grid could not be loaded.',
    },
    listing: {
      filtersActive: 'Active filters',
      filterColumn: 'Filter by {{column}}',
      sortTooltip: 'Click to toggle the order',
      sortBy: 'Sort by {{column}}',
      searchApply: 'Apply',
      searchChip: 'Code or name',
      pagination: '{{start}}–{{end}} of {{total}} projects',
      selectPage: 'Select every project on this page',
      selectRow: 'Select project {{code}}',
    },
    dialog: {
      close: 'Close',
      genericRefusal: 'The operation could not be completed. Try again.',
    },
    bulk: {
      gate: 'Open the gate',
      goLive: 'Put in service',
      writeOff: 'Write off projects',
      ineligible: {
        gate: 'The gate can only be opened on projects in Research or Development.',
        gateFramework:
          'The selected projects follow different accounting frameworks, each with its own ' +
          'checklist. Open the gate one framework at a time.',
        goLive: 'Only projects in Development can be put in service.',
        writeOff: 'Only projects in Research or Development can be written off.',
      },
      gateTitle_one: 'Open the gate for {{count}} project',
      gateTitle_other: 'Open the gate for {{count}} projects',
      goLiveTitle_one: 'Put {{count}} project in service',
      goLiveTitle_other: 'Put {{count}} projects in service',
      writeOffTitle_one: 'Write off {{count}} project',
      writeOffTitle_other: 'Write off {{count}} projects',
      gateReasonHint:
        'Required because at least one project is already in Development with a different ' +
        'certified date.',
      goLiveLifeTitle: 'Useful life by class',
      goLiveLifeHint:
        'Each project goes into service with its class’s useful life. To use a different one, ' +
        'put the project in service on its own.',
      goLiveLifeFromClass: '{{className}}: {{months}} months',
      goLiveLifeMissing:
        '{{className}} has no statutory rule. Put those projects in service on their own.',
      submit_one: 'Apply to {{count}} project',
      submit_other: 'Apply to {{count}} projects',
      running: 'Processing {{done}} of {{total}}…',
      resultSummary: '{{succeeded}} of {{total}} done.',
      resultAllOk: 'Every project was processed.',
      resultOk: 'Done',
      resultFailed: 'Failed',
      retryFailed: 'Retry the failed ones',
    },
  },
} as const;
